import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { apiClient } from './client';
import type {
  Bedarf,
  CreateBedarfDto,
  UpdateBedarfDto,
  QueryBedarfDto,
  BedarfStatistics,
  BedarfRecentEvent,
  SchoolCityCounts,
  ZuordnungKandidat,
} from '../types/bedarf';
import type { ApiResponse } from '../types/bewerber';
import type {
  MatchingBewerberResponse,
  MatchingMode,
} from '../types/kennenlernen';
import { DEFAULT_RADIUS_KM } from '../types/kennenlernen';
import type { RenderedEmail } from '../types/email';
import { bewerberKeys } from './bewerber';
import { bedarfActivityKeys, activityKeys } from './activities';
import { stellenausschreibungenKeys } from './stellenausschreibungen';

export interface MatchingBewerberParams {
  mode: MatchingMode;
  radiusKm: number;
  /** Only sent in Umkreis mode; the Stadt list has always been complete. */
  limit?: number;
}

const DEFAULT_MATCHING_PARAMS: MatchingBewerberParams = {
  mode: 'CITY',
  radiusKm: DEFAULT_RADIUS_KM,
};

// Query keys
export const bedarfeKeys = {
  all: ['bedarfe'] as const,
  lists: () => [...bedarfeKeys.all, 'list'] as const,
  list: (filters: QueryBedarfDto) => [...bedarfeKeys.lists(), filters] as const,
  kanban: () => [...bedarfeKeys.all, 'kanban'] as const,
  details: () => [...bedarfeKeys.all, 'detail'] as const,
  detail: (id: string) => [...bedarfeKeys.details(), id] as const,
  statistics: () => [...bedarfeKeys.all, 'statistics'] as const,
  /**
   * Prefix key covering every mode/radius/page variant of a Bedarf's matching
   * list. Invalidations must use this, not the parameterized key below, or a
   * mutation would only refresh whichever variant happens to be displayed.
   */
  matchingBewerberAll: (bedarfId: string) =>
    [...bedarfeKeys.all, 'matching-bewerber', bedarfId] as const,
  matchingBewerber: (bedarfId: string, params: MatchingBewerberParams) =>
    [...bedarfeKeys.matchingBewerberAll(bedarfId), params] as const,
  zuordnungKandidaten: (bedarfId: string, search: string) =>
    [...bedarfeKeys.all, 'zuordnung-kandidaten', bedarfId, search] as const,
  recentEvents: (days: number) => [...bedarfeKeys.all, 'dashboard', 'recent-events', days] as const,
  schoolCityCounts: () => [...bedarfeKeys.all, 'dashboard', 'school-cities'] as const,
};

// API functions
const fetchBedarfeList = async (query: QueryBedarfDto): Promise<ApiResponse<Bedarf[]>> => {
  const params = new URLSearchParams();
  if (query.search) params.append('search', query.search);
  if (query.status) params.append('status', query.status);
  if (query.priority) params.append('priority', query.priority);
  if (query.city) params.append('city', query.city);
  if (query.schoolCityId) params.append('schoolCityId', query.schoolCityId);
  if (query.assignedUserId) params.append('assignedUserId', query.assignedUserId);
  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.sortBy) params.append('sortBy', query.sortBy);
  if (query.sortOrder) params.append('sortOrder', query.sortOrder);

  const response = await apiClient.get(`/bedarfe?${params.toString()}`);
  return response.data;
};

const fetchBedarfeKanban = async (): Promise<ApiResponse<Bedarf[]>> => {
  const response = await apiClient.get('/bedarfe?limit=200&sortBy=statusChangedAt&sortOrder=desc');
  return response.data;
};

const fetchBedarf = async (id: string): Promise<ApiResponse<Bedarf>> => {
  const response = await apiClient.get(`/bedarfe/${id}`);
  return response.data;
};

const fetchBedarfeStatistics = async (): Promise<ApiResponse<BedarfStatistics>> => {
  const response = await apiClient.get('/bedarfe/statistics');
  return response.data;
};

const createBedarf = async (data: CreateBedarfDto): Promise<ApiResponse<Bedarf>> => {
  const response = await apiClient.post('/bedarfe', data);
  return response.data;
};

const updateBedarf = async ({ id, data }: { id: string; data: UpdateBedarfDto }): Promise<ApiResponse<Bedarf>> => {
  const response = await apiClient.patch(`/bedarfe/${id}`, data);
  return response.data;
};

const deleteBedarf = async (id: string): Promise<void> => {
  await apiClient.delete(`/bedarfe/${id}`);
};

const fetchMatchingBewerber = async (
  bedarfId: string,
  params: MatchingBewerberParams,
): Promise<MatchingBewerberResponse> => {
  const search = new URLSearchParams();

  // Left off entirely in Stadt mode, so that request stays byte-identical to
  // what it has always been.
  if (params.mode === 'RADIUS') {
    search.append('mode', 'RADIUS');
    search.append('radiusKm', String(params.radiusKm));
    if (params.limit) search.append('limit', String(params.limit));
  }

  const query = search.toString();
  const response = await apiClient.get(
    `/bedarfe/${bedarfId}/matching-bewerber${query ? `?${query}` : ''}`,
  );
  return response.data;
};

const calculateFahrzeit = async ({
  bewerberId,
  bedarfId,
}: {
  bewerberId: string;
  bedarfId: string;
}): Promise<ApiResponse<{ distanceKm: number; durationMinutes: number }>> => {
  const response = await apiClient.post('/geo/fahrzeit', {
    from: { kind: 'BEWERBER_HOME', id: bewerberId },
    to: { kind: 'BEDARF_SCHOOL', id: bedarfId },
  });
  return response.data;
};

const markBewerberNichtGeeignet = async ({
  bedarfId,
  bewerberId,
}: {
  bedarfId: string;
  bewerberId: string;
}): Promise<void> => {
  await apiClient.post(
    `/bedarfe/${bedarfId}/matching-bewerber/${bewerberId}/nicht-geeignet`,
  );
};

const fetchZuordnungKandidaten = async (
  bedarfId: string,
  search: string,
): Promise<ZuordnungKandidat[]> => {
  const response = await apiClient.get<ApiResponse<ZuordnungKandidat[]>>(
    `/bedarfe/${bedarfId}/zuordnung/kandidaten`,
    { params: search ? { search } : {} },
  );
  return response.data.data;
};

const zuordnenBewerber = async ({
  bedarfId,
  bewerberId,
}: {
  bedarfId: string;
  bewerberId: string;
  /** Whoever held the position before, so their views refresh too. */
  vorherBewerberId?: string;
}): Promise<void> => {
  await apiClient.post(`/bedarfe/${bedarfId}/zuordnung/${bewerberId}`);
};

const zuordnungAufheben = async ({
  bedarfId,
}: {
  bedarfId: string;
  /** Only used to refresh that Bewerber's views; the endpoint does not need it. */
  bewerberId?: string;
}): Promise<void> => {
  await apiClient.delete(`/bedarfe/${bedarfId}/zuordnung`);
};

const previewBedarfSenden = async ({
  bedarfId,
  bewerberId,
}: {
  bedarfId: string;
  bewerberId: string;
}): Promise<ApiResponse<RenderedEmail>> => {
  const response = await apiClient.post(
    `/bedarfe/${bedarfId}/send-to-bewerber/${bewerberId}/preview`,
  );
  return response.data;
};

const sendBedarfSenden = async ({
  bedarfId,
  bewerberId,
  data,
}: {
  bedarfId: string;
  bewerberId: string;
  data: { bodyHtml: string; subject: string };
}): Promise<ApiResponse<unknown>> => {
  const response = await apiClient.post(
    `/bedarfe/${bedarfId}/send-to-bewerber/${bewerberId}`,
    data,
  );
  return response.data;
};

const unmarkBewerberNichtGeeignet = async ({
  bedarfId,
  bewerberId,
}: {
  bedarfId: string;
  bewerberId: string;
}): Promise<void> => {
  await apiClient.delete(
    `/bedarfe/${bedarfId}/matching-bewerber/${bewerberId}/nicht-geeignet`,
  );
};

// React Query Hooks

export function useBedarfeList(query: QueryBedarfDto = {}) {
  return useQuery({
    queryKey: bedarfeKeys.list(query),
    queryFn: () => fetchBedarfeList(query),
  });
}

export function useBedarfeKanban() {
  return useQuery({
    queryKey: bedarfeKeys.kanban(),
    queryFn: fetchBedarfeKanban,
  });
}

export function useBedarf(id: string) {
  return useQuery({
    queryKey: bedarfeKeys.detail(id),
    queryFn: () => fetchBedarf(id),
    enabled: !!id,
  });
}

export function useBedarfeStatistics() {
  return useQuery({
    queryKey: bedarfeKeys.statistics(),
    queryFn: fetchBedarfeStatistics,
  });
}

export function useCreateBedarf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createBedarf,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bedarfeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bedarfeKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bedarfeKeys.statistics() });
      // Der Status entscheidet, ob und in welcher Spalte der Bedarf auf dem
      // Stellenausschreibungs-Board steht und ob er dessen Ticker auslöst.
      queryClient.invalidateQueries({ queryKey: stellenausschreibungenKeys.all });
    },
  });
}

export function useUpdateBedarf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBedarf,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: bedarfeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bedarfeKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bedarfeKeys.detail(data.data.id) });
      queryClient.invalidateQueries({ queryKey: bedarfeKeys.statistics() });
      // Der Status entscheidet, ob und in welcher Spalte der Bedarf auf dem
      // Stellenausschreibungs-Board steht und ob er dessen Ticker auslöst.
      queryClient.invalidateQueries({ queryKey: stellenausschreibungenKeys.all });
      // Refresh the change history so field updates (e.g. onboarding checkboxes) show immediately.
      queryClient.invalidateQueries({ queryKey: bedarfActivityKeys.timelines(data.data.id) });
    },
  });
}

export function useDeleteBedarf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBedarf,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bedarfeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bedarfeKeys.kanban() });
      queryClient.invalidateQueries({ queryKey: bedarfeKeys.statistics() });
      // Der Status entscheidet, ob und in welcher Spalte der Bedarf auf dem
      // Stellenausschreibungs-Board steht und ob er dessen Ticker auslöst.
      queryClient.invalidateQueries({ queryKey: stellenausschreibungenKeys.all });
    },
  });
}

/**
 * `params` is the third argument on purpose: existing callers that only pass
 * `(bedarfId, enabled)` keep the previous Stadt-mode behaviour unchanged.
 */
export function useMatchingBewerber(
  bedarfId: string,
  enabled = true,
  params: MatchingBewerberParams = DEFAULT_MATCHING_PARAMS,
) {
  return useQuery({
    queryKey: bedarfeKeys.matchingBewerber(bedarfId, params),
    queryFn: () => fetchMatchingBewerber(bedarfId, params),
    enabled: !!bedarfId && enabled,
    // Keeps the current rows on screen while a new radius or page loads.
    placeholderData: keepPreviousData,
  });
}

export function useFahrzeit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: calculateFahrzeit,
    onSuccess: (_data, variables) => {
      // The list re-reads the route cache, so the new travel time shows up.
      queryClient.invalidateQueries({
        queryKey: bedarfeKeys.matchingBewerberAll(variables.bedarfId),
      });
    },
  });
}

export function useMarkBewerberNichtGeeignet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markBewerberNichtGeeignet,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: bedarfeKeys.matchingBewerberAll(variables.bedarfId),
      });
      queryClient.invalidateQueries({
        queryKey: activityKeys.timelines(variables.bewerberId),
      });
    },
  });
}

/**
 * Both sides of the link change at once, so both sides are invalidated: the
 * Bedarf detail (its Schulbegleiter card), the matching list (badge and button),
 * and the Bewerber's own detail page and timeline.
 */
function invalidateZuordnung(
  queryClient: ReturnType<typeof useQueryClient>,
  bedarfId: string,
  ...bewerberIds: (string | undefined)[]
) {
  queryClient.invalidateQueries({ queryKey: bedarfeKeys.detail(bedarfId) });
  queryClient.invalidateQueries({ queryKey: bedarfeKeys.lists() });
  queryClient.invalidateQueries({ queryKey: bedarfeKeys.kanban() });
  queryClient.invalidateQueries({
    queryKey: bedarfeKeys.matchingBewerberAll(bedarfId),
  });
  queryClient.invalidateQueries({ queryKey: bedarfActivityKeys.timelines(bedarfId) });
  queryClient.invalidateQueries({
    queryKey: [...bedarfeKeys.all, 'zuordnung-kandidaten'],
  });
  for (const id of bewerberIds) {
    if (!id) continue;
    queryClient.invalidateQueries({ queryKey: bewerberKeys.detail(id) });
    queryClient.invalidateQueries({ queryKey: activityKeys.timelines(id) });
  }
}

/**
 * Candidates for the Schulbegleiter picker. Only fetched while the picker is
 * open; the search runs on the server, since there can be hundreds of Bewerber.
 */
export function useZuordnungKandidaten(bedarfId: string, search: string, enabled: boolean) {
  return useQuery({
    queryKey: bedarfeKeys.zuordnungKandidaten(bedarfId, search),
    queryFn: () => fetchZuordnungKandidaten(bedarfId, search),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useZuordnenBewerber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: zuordnenBewerber,
    onSuccess: (_data, variables) =>
      invalidateZuordnung(
        queryClient,
        variables.bedarfId,
        variables.bewerberId,
        variables.vorherBewerberId,
      ),
  });
}

export function useZuordnungAufheben() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: zuordnungAufheben,
    onSuccess: (_data, variables) =>
      invalidateZuordnung(queryClient, variables.bedarfId, variables.bewerberId),
  });
}

export function usePreviewBedarfSenden() {
  return useMutation({
    mutationFn: previewBedarfSenden,
  });
}

export function useSendBedarfSenden() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendBedarfSenden,
    onSuccess: (_data, variables) => {
      // Refresh the matching list so the "Bedarf bereits gesendet" badge appears
      queryClient.invalidateQueries({
        queryKey: bedarfeKeys.matchingBewerberAll(variables.bedarfId),
      });
      // The email is logged on the Bewerber timeline
      queryClient.invalidateQueries({
        queryKey: activityKeys.timelines(variables.bewerberId),
      });
    },
  });
}

export function useUnmarkBewerberNichtGeeignet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unmarkBewerberNichtGeeignet,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: bedarfeKeys.matchingBewerberAll(variables.bedarfId),
      });
      queryClient.invalidateQueries({
        queryKey: activityKeys.timelines(variables.bewerberId),
      });
    },
  });
}

const fetchBedarfeRecentEvents = async (
  days: number,
): Promise<ApiResponse<BedarfRecentEvent[]>> => {
  const response = await apiClient.get(
    `/bedarfe/dashboard/recent-events?days=${days}`,
  );
  return response.data;
};

const fetchBedarfeSchoolCityCounts = async (): Promise<
  ApiResponse<SchoolCityCounts[]>
> => {
  const response = await apiClient.get('/bedarfe/dashboard/school-cities');
  return response.data;
};

export function useBedarfeRecentEvents(days = 7) {
  return useQuery({
    queryKey: bedarfeKeys.recentEvents(days),
    queryFn: () => fetchBedarfeRecentEvents(days),
    staleTime: 30_000,
  });
}

export function useBedarfeSchoolCityCounts() {
  return useQuery({
    queryKey: bedarfeKeys.schoolCityCounts(),
    queryFn: fetchBedarfeSchoolCityCounts,
    staleTime: 30_000,
  });
}
